import { formatCostAmount, formatVolume, NO_DATA_TEXT } from "./format";

export interface ConsumptionReportGridRow {
	[field: string]: string | number | boolean | null | undefined;
	id: string;
	period?: string;
	mill?: string;
	section?: string;
	event?: string;
	eventStart?: string;
	eventEnd?: string;
	gallonsPerTon?: number | null;
	gallonsPerBillet?: number | null;
	gallons: number | null;
	costPerTon?: number | null;
	cost: number | null;
	isCostApproximate: boolean;
	missingMeters: string;
}

interface ConsumptionReportGridRowInput {
	id: string;
	period?: string;
	mill?: string;
	section?: string;
	event?: string;
	eventStart?: string;
	eventEnd?: string;
	gallonsPerTon?: number | null;
	gallonsPerBillet?: number | null;
	meterIds: string[];
	meterGallons: Record<string, number | null>;
	meterCoverage: Record<string, number | null>;
	gallons: number | null;
	costPerTon?: number | null;
	cost: number | null;
	isCostApproximate: boolean;
}

interface ConsumptionReportTotalRowInput {
	meterIds: string[];
	meterTotalGallons: Record<string, number | null>;
	grandTotalGallons: number | null;
	grandGallonsPerTon: number | null;
	grandGallonsPerBillet: number | null;
	grandTotalCost: number | null;
	grandCostPerTon: number | null;
	isCostApproximate: boolean;
	isSection: boolean;
}

export const getConsumptionReportMeterField = (index: number) =>
	`meter_${index}`;

export const getConsumptionReportMeterCoverageField = (index: number) =>
	`meter_${index}_coverage`;

export const formatConsumptionReportGallons = (
	value: number | null | undefined,
) => formatVolume(value) ?? NO_DATA_TEXT;

export const formatConsumptionReportCost = (
	value: number | null | undefined,
	isApproximate: boolean | null | undefined,
) => {
	const amount = formatCostAmount(value);
	if (amount === null) return NO_DATA_TEXT;
	return isApproximate ? `≈${amount}` : amount;
};

export const createConsumptionReportGridRow = ({
	meterIds,
	meterGallons,
	meterCoverage,
	...row
}: ConsumptionReportGridRowInput): ConsumptionReportGridRow => {
	const missingMeterIds = meterIds.filter(
		(meterId) =>
			meterGallons[meterId] === null || meterGallons[meterId] === undefined,
	);
	const gridRow: ConsumptionReportGridRow = {
		...row,
		missingMeters: missingMeterIds.join(", "),
	};
	meterIds.forEach((meterId, index) => {
		gridRow[getConsumptionReportMeterField(index)] =
			meterGallons[meterId] ?? null;
		gridRow[getConsumptionReportMeterCoverageField(index)] =
			meterCoverage[meterId] ?? null;
	});
	return gridRow;
};

export const createConsumptionReportTotalRow = ({
	meterIds,
	meterTotalGallons,
	grandTotalGallons,
	grandGallonsPerTon,
	grandGallonsPerBillet,
	grandTotalCost,
	grandCostPerTon,
	isCostApproximate,
	isSection,
}: ConsumptionReportTotalRowInput): ConsumptionReportGridRow =>
	createConsumptionReportGridRow({
		id: "total",
		period: isSection ? undefined : "Total",
		mill: undefined,
		section: undefined,
		event: isSection ? "Total" : undefined,
		eventStart: undefined,
		eventEnd: undefined,
		gallonsPerTon: grandGallonsPerTon,
		gallonsPerBillet: grandGallonsPerBillet,
		meterIds,
		meterGallons: meterTotalGallons,
		gallons: grandTotalGallons,
		costPerTon: grandCostPerTon,
		cost: grandTotalCost,
		meterCoverage: {},
		isCostApproximate,
	});
